/* import __COLOCATED_TEMPLATE__ from './channel-limit.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { post } from '../../../lib/ajax';
import { action } from '@ember/object';
import { ref } from 'ember-ref-bucket';
import type IntlService from 'embercom/services/intl';
import type AppConversationCapacityLimit from 'embercom/models/app-conversation-capacity-limit';

type ChannelType = 'email' | 'messenger';

interface ChannelLimits {
  email?: number;
  messenger?: number;
}

interface WorkloadManagementSettings {
  channel_limits?: ChannelLimits | null;
}

interface Admin {
  id: string;
  name?: string;
  workload_management_settings?: WorkloadManagementSettings;
}

interface Signature {
  Args: {
    admin: Admin;
    workspaceSettings: AppConversationCapacityLimit;
    channelType: ChannelType;
  };
  Element: HTMLDivElement;
}

export default class ChannelLimit extends Component<Signature> {
  @service declare permissionsService: any;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomConfirmService: any;
  @service declare intercomEventService: any;
  @service declare modelDataCacheService: any;

  @tracked channelLimitValue = String(
    this.args.admin?.workload_management_settings?.channel_limits?.[this.args.channelType] ?? '',
  );
  @tracked isEditingChannelLimit = false;
  @tracked valueIsModified = false;
  @tracked isReverting = false;

  @ref('channel-limit-input') inputElement: any;

  get canChangeChannelLimit(): boolean {
    return this.permissionsService.currentAdminCan('can_manage_workload_management');
  }

  get channelLimitToInt(): number {
    return parseInt(this.channelLimitValue, 10);
  }

  get channelLimit(): number | null | undefined {
    return this.args.admin?.workload_management_settings?.channel_limits?.[this.args.channelType];
  }

  get currentWorkspaceLimit(): number | undefined {
    return (
      this.args.workspaceSettings?.channelLimits?.[this.args.channelType] ??
      this.args.workspaceSettings?.capacityLimit ??
      0
    );
  }

  get hasCustomLimit(): boolean {
    return this.channelLimit !== undefined && this.channelLimit !== null;
  }

  get channelLimitHasChanged(): boolean {
    return this.valueIsModified;
  }

  get channelLimitInvalid(): boolean {
    return (
      !this.channelLimitValue || this.channelLimitToInt > 1_000_000 || this.channelLimitToInt < 0
    );
  }

  get disableSubmitButton(): boolean {
    return this.channelLimitInvalid || !this.channelLimitHasChanged;
  }

  @action
  async changeChannelLimit() {
    if ((this.channelLimitInvalid || !this.channelLimitHasChanged) && !this.isReverting) {
      return;
    }

    let newLimitValue = !this.isReverting ? this.channelLimitToInt : null;
    let channelLimits = { [this.args.channelType]: newLimitValue };

    try {
      await post(`/ember/admins/change_channel_limits`, {
        channel_limits: channelLimits,
        admin_ids: [this.args.admin.id],
        id: this.appService.app.currentAdmin.id,
        app_id: this.appService.app.id,
      });

      let mergedLimits = {
        ...(this.args.admin.workload_management_settings?.channel_limits ?? {}),
        ...channelLimits,
      };
      if (this.isReverting) {
        delete mergedLimits[this.args.channelType];
      }
      this.args.admin.workload_management_settings = {
        ...this.args.admin.workload_management_settings,
        channel_limits: mergedLimits,
      };

      if (!this.isReverting) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('settings.teammate-workload.channel-limit.success', {
            teammate: this.args.admin?.name,
          }),
        );
      } else {
        this.notificationsService.notifyConfirmation(
          this.intl.t('settings.teammate-workload.channel-limit.revert-to-workspace-success', {
            teammate: this.args.admin?.name,
            workspaceLimit: this.currentWorkspaceLimit,
          }),
        );
      }
      this.resetState();
    } catch (error: unknown) {
      if (error instanceof Object && 'jqXHR' in error) {
        let ajaxError = error as { jqXHR: { responseJSON: { errors: string } } };
        this.notificationsService.notifyError(ajaxError.jqXHR.responseJSON.errors);
      }
    } finally {
      this.appService.app.updateLocalCache(this.modelDataCacheService);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'channel_limit',
        place: 'teammate_workload',
        teammate_id: this.args.admin.id,
        channel_type: this.args.channelType,
        channel_limit: newLimitValue,
      });
    }
  }

  @action unfocusInput(focusEvent: FocusEvent) {
    if ((focusEvent.relatedTarget as HTMLElement)?.getAttribute('type') === 'submit') {
      return;
    }

    if (!this.channelLimitHasChanged) {
      return;
    }

    this.triggerConfirmModal();
  }

  @action async triggerConfirmModal() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('settings.teammate-workload.channel-limit-confirm-modal.title'),
      body: this.intl.t('settings.teammate-workload.channel-limit-confirm-modal.body', {
        name: this.args.admin.name,
      }),
      cancelButtonText: this.intl.t(
        'settings.teammate-workload.channel-limit-confirm-modal.cancel-button',
      ),
      confirmButtonText: this.intl.t(
        'settings.teammate-workload.channel-limit-confirm-modal.confirm-button',
      ),
    });

    if (confirmed) {
      this.resetState();
    } else {
      this.inputElement.focus();
    }
  }

  @action
  async revertToWorkspaceLimit() {
    this.isReverting = true;
    await this.changeChannelLimit();
    this.isReverting = false;
  }

  resetState() {
    this.isEditingChannelLimit = false;
    this.channelLimitValue = String(
      this.args.admin?.workload_management_settings?.channel_limits?.[this.args.channelType] ?? '',
    );
    this.valueIsModified = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::TeammateWorkload::ChannelLimit': typeof ChannelLimit;
    'inbox/teammate-workload/channel-limit': typeof ChannelLimit;
  }
}
